// Whether a collapsible side card is open (S136), remembered per browser for each kind of card
// (one choice for every group's Resources card, not one per group), like the group's details
// panel (tasks/group/panelState.ts).
import { useEffect, useState } from "react";

import { useCardGroupCommand } from "./cardGroup";

const key = (kind: string) => `ocs.card.${kind}`;

export function savedCardOpen(kind: string): boolean {
  try {
    return localStorage.getItem(key(kind)) !== "collapsed";
  } catch {
    // Storage can be blocked (private mode); start open.
    return true;
  }
}

/** `remember: false` (S144): the card starts as `defaultOpen` on every visit and nothing is saved. */
export function useCardOpen(
  kind: string,
  { remember = true, defaultOpen = true }: { remember?: boolean; defaultOpen?: boolean } = {},
): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => (remember ? savedCardOpen(kind) : defaultOpen));
  const choose = (next: boolean) => {
    setOpen(next);
    if (remember) saveCardOpen(kind, next);
  };
  // Expand all / Collapse all (S163): follow each command the page gives, once (a card that
  // appears after one follows it too), and remember the result as a header click would.
  const command = useCardGroupCommand();
  const [seen, setSeen] = useState<number>();
  if (command && command.seq !== seen) {
    setSeen(command.seq);
    setOpen(command.open);
  }
  useEffect(() => {
    if (seen !== undefined && remember) saveCardOpen(kind, open);
  }, [seen, open, remember, kind]);
  return [open, choose];
}

function saveCardOpen(kind: string, open: boolean) {
  try {
    localStorage.setItem(key(kind), open ? "expanded" : "collapsed");
  } catch {
    // Not persisting the choice is fine.
  }
}
